import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { loginRequest } from '@lib/msal';

export default function Login() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [sessionExpired, setSessionExpired] = useState(false);

  // Check if session expired (set by idle timeout)
  useEffect(() => {
    const expired = sessionStorage.getItem('sca-uk-session-expired');
    if (expired === 'true') {
      setSessionExpired(true);
      // Clear the flag so it doesn't show again on manual refresh
      sessionStorage.removeItem('sca-uk-session-expired');
    }
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sca-purple-500 to-sca-pink-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        {/* Session expired message */}
        {sessionExpired && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-amber-800">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium">Your session has expired due to inactivity. Please sign in again.</p>
            </div>
          </div>
        )}

        {/* Logo placeholder */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sca-purple-100 mb-4">
            <svg
              className="w-8 h-8 text-sca-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">SCA UK Operations Hub</h1>
          <p className="text-gray-500 mt-2">
            Sign in to manage volunteers, resources, and operations
          </p>
        </div>

        {/* Login button */}
        <button
          onClick={handleLogin}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
        >
          <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
            <path d="M0 0h10v10H0V0z" fill="#F25022" />
            <path d="M11 0h10v10H11V0z" fill="#7FBA00" />
            <path d="M0 11h10v10H0V11z" fill="#00A4EF" />
            <path d="M11 11h10v10H11V11z" fill="#FFB900" />
          </svg>
          Sign in with Microsoft
        </button>

        {/* Help text */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Use your SCA UK Microsoft account to sign in.
          <br />
          Contact your administrator if you need access.
        </p>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} Sudden Cardiac Arrest UK.
            All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
